export interface ErrorReport {
  message: string
  stack?: string
  context?: Record<string, unknown>
  userId?: string
  timestamp: number
  severity: 'fatal' | 'error' | 'warning' | 'info'
}

export interface ErrorReporter {
  report(error: Error | string, context?: Record<string, unknown>, severity?: ErrorReport['severity']): void
  setUser(userId: string): void
  clearUser(): void
}

export class ConsoleErrorReporter implements ErrorReporter {
  private userId?: string
  report(error: Error | string, context?: Record<string, unknown>, severity: ErrorReport['severity'] = 'error'): void {
    const errorReport: ErrorReport = {
      message: typeof error === 'string' ? error : error.message,
      stack: typeof error === 'object' ? error.stack : undefined,
      context,
      userId: this.userId,
      timestamp: Date.now(),
      severity
    }
    // eslint-disable-next-line no-console
    console.error('[ErrorReporter]', errorReport)
  }
  setUser(userId: string): void { this.userId = userId }
  clearUser(): void { this.userId = undefined }
}

export class ErrorBoundaryReporter {
  private static reporter: ErrorReporter = new ConsoleErrorReporter()
  static setReporter(reporter: ErrorReporter): void { this.reporter = reporter }
  static reportError(error: Error, errorInfo?: { componentStack?: string | null }): void {
    this.reporter.report(error, { errorBoundary: true, componentStack: errorInfo?.componentStack }, 'fatal')
  }
  static getReporter(): ErrorReporter { return this.reporter }
}

export function setupGlobalErrorHandlers(reporter: ErrorReporter): void {
  // Avoid duplicate registrations in dev hot-reload
  const w = window as any
  if (w.__errorHandlersRegistered) return
  w.__errorHandlersRegistered = true

  window.addEventListener('unhandledrejection', (event) => {
    reporter.report(new Error(`Unhandled Promise Rejection: ${event.reason}`), { promise: true }, 'error')
  })
  window.addEventListener('error', (event) => {
    reporter.report(event.error || new Error(event.message), { filename: event.filename, lineno: event.lineno, colno: event.colno }, 'fatal')
  })
}
